import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
	AlertCircle,
	ArrowLeft,
	ArrowRight,
	BarChart3,
	Building2,
	CalendarRange,
	ChevronDown,
	Check,
	CircleDollarSign,
	CircleStop,
	Copy,
	CreditCard,
	Download,
	FileText,
	Infinity as InfinityIcon,
	Landmark,
	ListChecks,
	Pencil,
	Plus,
	RefreshCw,
	Search,
	Smartphone,
	Store,
	Target,
	Trash2,
	Package,
	UserRound,
	WalletCards,
	X,
	XCircle
} from 'lucide-react';
import Input from '../../components/Input';
import SellerCommissionTargetSelector from '../../components/SellerCommissionTargetSelector';
import { Dialog } from '../../components/Dialog';
import { TabBar } from '../../components/TabBar';
import { Button } from '../../shadcn/Button';
import { DateTimePicker } from '../../shadcn/DateTimePicker';
import { Switch } from '../../shadcn/Switch';
import { cn } from '../../shadcn/utils';
import { GlobalContext } from '../../context/GlobalContext';
import useFetch, { callAPI } from '../../hooks/api';
import {
	GetRoutePermissions,
	userCanConfigureSellerCommissions,
	userCanManageSellerCommissionPayouts
} from '../../hooks/firebase';
import { format_currency } from '../../utils/formatters';
import { CampaignEarningSetup, SellerDetail, StoreDashboard } from '../comisiones-vendedores';
import {
	SellerCommissionCampaign,
	SellerCommissionCampaignInput,
	SellerCommissionCampaignResults,
	SellerCommissionCampaignStatus,
	SellerCommissionPayoutRequest,
	SellerCommissionPayoutTransitionStatus,
	SellerCommissionProductCategory,
	SellerCommissionTarget,
	SellerCommissionTier,
	SellerCommissionVolumeTier
} from '../../types/seller-commissions';

type CampaignFilter = 'all' | SellerCommissionCampaignStatus;
type PayoutFilter = 'all' | SellerCommissionPayoutRequest['status'];
type CampaignStep = 1 | 2 | 3 | 4;

const campaignStatus: Record<SellerCommissionCampaignStatus, string> = {
	upcoming: 'Programada',
	active: 'Activa',
	ended: 'Finalizada',
	stopped: 'Detenida'
};

const campaignStatusClass: Record<SellerCommissionCampaignStatus, string> = {
	upcoming: 'bg-blue-500/10 text-blue-300 light:text-blue-700',
	active: 'bg-emerald-500/10 text-emerald-300 light:text-emerald-700',
	ended: 'bg-neutral-800 text-neutral-300',
	stopped: 'bg-amber-500/10 text-amber-300 light:text-amber-700'
};

const payoutStatus: Record<SellerCommissionPayoutRequest['status'], string> = {
	requested: 'Solicitada',
	processing: 'Procesando',
	paid: 'Pagada',
	rejected: 'Rechazada'
};

const payoutStatusClass: Record<SellerCommissionPayoutRequest['status'], string> = {
	requested: 'bg-blue-500/10 text-blue-300',
	processing: 'bg-amber-500/10 text-amber-300',
	paid: 'bg-emerald-500/10 text-emerald-300',
	rejected: 'bg-red-500/10 text-red-300'
};

const validPayoutReceipt = (file: File) =>
	file.name.toLowerCase().endsWith('.pdf') &&
	['', 'application/pdf', 'application/x-pdf'].includes(file.type.toLowerCase()) &&
	file.size > 0 &&
	file.size <= 10 * 1024 * 1024;

const emptyCampaign = (defaultMinPayoutAmount: number): SellerCommissionCampaignInput => ({
	name: '',
	starts_at: '',
	ends_at: '',
	target_keys: [],
	plan: { mode: 'fixed_per_sale', fixed_amount: 0, tiers: [], volume_tiers: [] },
	product_categories: ['phones'],
	payout_recipient: 'seller',
	min_payout_amount: defaultMinPayoutAmount
});

export default function SellerCommissionsAdmin() {
	const { userData, internalCountry, isAdminAccount } = useContext(GlobalContext);
	const country = internalCountry.get() || userData?.country || 'venezuela';
	const routePermissions = GetRoutePermissions();
	const canConfigure = userCanConfigureSellerCommissions(userData, routePermissions, country, isAdminAccount);
	const canManagePayouts = userCanManageSellerCommissionPayouts(userData, routePermissions, country);
	const [activeTab, setActiveTab] = useState('campaigns');
	const [draft, setDraft] = useState<SellerCommissionCampaignInput | null>(null);
	const [campaignStep, setCampaignStep] = useState<CampaignStep>(1);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [editingCampaign, setEditingCampaign] = useState<SellerCommissionCampaign | null>(null);
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState<string | null>(null);
	const [campaignToStop, setCampaignToStop] = useState<SellerCommissionCampaign | null>(null);
	const [resultsCampaign, setResultsCampaign] = useState<SellerCommissionCampaign | null>(null);
	const [resultsSeller, setResultsSeller] = useState<{ masterEmail: string; sellerEmail: string } | null>(null);
	const resultPageCursors = useRef<Array<string | null>>([null]);
	const [stopping, setStopping] = useState(false);
	const [updatingRequest, setUpdatingRequest] = useState<string | null>(null);
	const [payoutFilter, setPayoutFilter] = useState<PayoutFilter>('all');
	const [payoutOffset, setPayoutOffset] = useState(0);
	const [payoutCursor, setPayoutCursor] = useState<string | undefined>();
	const [payoutCursorHistory, setPayoutCursorHistory] = useState<(string | undefined)[]>([]);
	const payoutPagingRef = useRef(false);
	const [payoutPaging, setPayoutPaging] = useState(false);
	const [pendingPayoutUpdate, setPendingPayoutUpdate] = useState<{
		request: SellerCommissionPayoutRequest;
		status: SellerCommissionPayoutTransitionStatus;
	} | null>(null);

	const {
		data: campaignData,
		loading: loadingCampaigns,
		error: campaignError,
		refetch: refetchCampaigns,
		setPayload: setCampaignPayload
	} = useFetch('https://api.krece.app/storeprofile/seller_commissions/campaigns/', 'GET', canConfigure, {
		country,
		include_targets: false
	});
	const {
		data: campaignResults,
		loading: loadingCampaignResults,
		error: campaignResultsError,
		refetch: refetchCampaignResults,
		setPayload: setCampaignResultsPayload
	} = useFetch(
		'https://api.krece.app/storeprofile/seller_commissions/campaign_results/',
		'GET',
		canConfigure && !!resultsCampaign,
		{
			country,
			campaign_id: resultsCampaign?.id || '',
			batch_size: 25,
			page_index: 0
		}
	);
	const {
		data: payoutData,
		loading: loadingPayouts,
		fetched: payoutsFetched,
		error: payoutError,
		refetch: refetchPayouts,
		setPayload: setPayoutPayload
	} = useFetch('https://api.krece.app/storeprofile/seller_commissions/payout_requests/', 'GET', canManagePayouts && activeTab === 'payouts', {
		country,
		batch_size: 100,
		offset: payoutOffset,
		...(payoutFilter !== 'all' && { status: payoutFilter }),
		...(payoutCursor && { cursor: payoutCursor })
	});

	useEffect(() => {
		if (!userData?.email) return;
		if (canConfigure) setActiveTab('campaigns');
		else if (canManagePayouts) setActiveTab('payouts');
	}, [canConfigure, canManagePayouts, userData?.email]);

	useEffect(() => {
		setPayoutOffset(0);
		setPayoutCursor(undefined);
		setPayoutCursorHistory([]);
		setResultsCampaign(null);
		setResultsSeller(null);
		resultPageCursors.current = [null];
		setCampaignPayload({ country, include_targets: false });
	}, [country, setCampaignPayload]);

	useEffect(() => {
		setPayoutPayload({
			country,
			batch_size: 100,
			offset: payoutOffset,
			...(payoutFilter !== 'all' && { status: payoutFilter }),
			...(payoutCursor && { cursor: payoutCursor })
		});
	}, [country, payoutCursor, payoutFilter, payoutOffset, setPayoutPayload]);

	useEffect(() => {
		if (activeTab !== 'campaigns' || !canConfigure) return;
		const interval = window.setInterval(() => {
			if (document.visibilityState === 'visible') refetchCampaigns();
		}, 120_000);
		return () => window.clearInterval(interval);
	}, [activeTab, canConfigure, refetchCampaigns]);

	useEffect(() => {
		if (activeTab !== 'payouts' || !canManagePayouts) return;
		const interval = window.setInterval(() => {
			if (document.visibilityState === 'visible') refetchPayouts();
		}, 60_000);
		return () => window.clearInterval(interval);
	}, [activeTab, canManagePayouts, refetchPayouts]);

	useEffect(() => {
		if (!payoutPaging || !payoutsFetched || (!payoutError && payoutData?.offset !== payoutOffset)) return;
		payoutPagingRef.current = false;
		setPayoutPaging(false);
	}, [payoutData?.offset, payoutError, payoutOffset, payoutPaging, payoutsFetched]);

	const openCreate = () => {
		if (!campaignData) return;
		setEditingId(null);
		setEditingCampaign(null);
		setCampaignStep(1);
		setDraft(emptyCampaign(campaignData.default_min_payout_amount));
	};

	const openEdit = (campaign: SellerCommissionCampaign) => {
		setEditingId(campaign.id);
		setEditingCampaign(campaign);
		setCampaignStep(1);
		setDraft({
			name: campaign.name,
			starts_at: campaign.starts_at,
			ends_at: campaign.ends_at,
			target_keys: campaign.target_keys,
			plan: { ...campaign.plan, volume_tiers: campaign.plan.volume_tiers || [] },
			product_categories: campaign.product_categories || ['phones', 'iphones'],
			payout_recipient: campaign.payout_recipient,
			min_payout_amount: campaign.min_payout_amount
		});
	};

	const closeCampaign = () => {
		setDraft(null);
		setEditingId(null);
		setEditingCampaign(null);
		setCampaignStep(1);
	};

	const saveCampaign = async () => {
		if (!userData || !draft) return;
		const validationError = validateCampaign(draft, editingCampaign);
		if (validationError) {
			setMessage(validationError);
			return;
		}
		if (!isRestrictedCampaignEdit(editingCampaign) && findOverlappingCampaign(draft, campaignData?.campaigns || [], editingId)) {
			setMessage('El alcance y las fechas se solapan con otra campaña. Ajuste la vigencia o las tiendas seleccionadas.');
			return;
		}
		setSaving(true);
		const [response, error] = editingId
			? await callAPI('https://api.krece.app/storeprofile/seller_commissions/campaigns/', 'PUT', { ...draft, id: editingId }, userData, country)
			: await callAPI('https://api.krece.app/storeprofile/seller_commissions/campaigns/', 'POST', draft, userData, country);
		setSaving(false);
		if (error || !response) {
			setMessage(error || 'No se pudo guardar la campaña.');
			return;
		}
		const edited = !!editingId;
		closeCampaign();
		setMessage(edited ? 'Campaña actualizada.' : 'Campaña creada.');
		refetchCampaigns();
	};

	const stopCampaign = async () => {
		if (!userData || !campaignToStop) return;
		setStopping(true);
		const [, error] = await callAPI(
			'https://api.krece.app/storeprofile/seller_commissions/campaigns/',
			'PATCH',
			{ id: campaignToStop.id },
			userData,
			country
		);
		setStopping(false);
		setCampaignToStop(null);
		if (error) {
			setMessage(error);
			return;
		}
		setMessage('Campaña detenida. Las ventas posteriores no generarán comisión.');
		refetchCampaigns();
	};

	const updatePayout = async (
		request: SellerCommissionPayoutRequest,
		status: SellerCommissionPayoutTransitionStatus,
		notes = '',
		receipt?: File
	) => {
		if (!userData) return;
		setUpdatingRequest(request.id);
		let payload: { id: string; status: SellerCommissionPayoutTransitionStatus; notes?: string } | FormData = {
			id: request.id,
			status,
			...(notes.trim() && { notes: notes.trim() })
		};
		if (receipt) {
			const formData = new FormData();
			formData.append('id', request.id);
			formData.append('status', status);
			if (notes.trim()) formData.append('notes', notes.trim());
			formData.append('receipt', receipt);
			payload = formData;
		}
		const [, error] = await callAPI(
			'https://api.krece.app/storeprofile/seller_commissions/payout_requests/',
			'PATCH',
			payload,
			userData,
			country
		);
		setUpdatingRequest(null);
		setPendingPayoutUpdate(null);
		if (error) {
			setMessage(error);
			return;
		}
		refetchPayouts();
	};

	const downloadReceipt = async (request: SellerCommissionPayoutRequest) => {
		if (!userData) return;
		const [receipt, error] = await callAPI(
			'https://api.krece.app/storeprofile/seller_commissions/payout_receipt/',
			'GET',
			{ id: request.id },
			userData,
			country
		);
		if (error || !receipt) {
			setMessage(error || 'No se pudo descargar el comprobante.');
			return;
		}
		const link = document.createElement('a');
		link.href = receipt.download_url;
		link.download = receipt.filename;
		document.body.appendChild(link);
		link.click();
		link.remove();
	};

	const changePayoutFilter = (filter: PayoutFilter) => {
		payoutPagingRef.current = false;
		setPayoutPaging(false);
		setPayoutFilter(filter);
		setPayoutOffset(0);
		setPayoutCursor(undefined);
		setPayoutCursorHistory([]);
	};

	const nextPayoutPage = () => {
		if (payoutPagingRef.current || !payoutData?.next_cursor) return;
		payoutPagingRef.current = true;
		setPayoutPaging(true);
		setPayoutCursorHistory((history) => [...history, payoutCursor]);
		setPayoutCursor(payoutData.next_cursor || undefined);
		setPayoutOffset((offset) => offset + (payoutData.batch_size || 100));
	};

	const previousPayoutPage = () => {
		if (payoutPagingRef.current || !payoutCursorHistory.length) return;
		payoutPagingRef.current = true;
		setPayoutPaging(true);
		const previousCursor = payoutCursorHistory[payoutCursorHistory.length - 1];
		setPayoutCursorHistory((history) => history.slice(0, -1));
		setPayoutCursor(previousCursor);
		setPayoutOffset((offset) => Math.max(0, offset - (payoutData?.batch_size || 100)));
	};

	const openCampaignResults = (campaign: SellerCommissionCampaign) => {
		setResultsCampaign(campaign);
		setResultsSeller(null);
		resultPageCursors.current = [null];
		setCampaignResultsPayload({ country, campaign_id: campaign.id, batch_size: 25, page_index: 0 });
	};

	const closeCampaignResults = () => {
		setResultsCampaign(null);
		setResultsSeller(null);
		resultPageCursors.current = [null];
	};

	const selectResultSeller = (masterEmail: string, sellerEmail: string) => {
		if (!resultsCampaign) return;
		setResultsSeller({ masterEmail, sellerEmail });
		resultPageCursors.current = [null];
		setCampaignResultsPayload({
			country,
			campaign_id: resultsCampaign.id,
			master_store_email: masterEmail,
			seller_email: sellerEmail,
			batch_size: 25,
			page_index: 0
		});
	};

	const closeResultSeller = () => {
		if (!resultsCampaign) return;
		setResultsSeller(null);
		resultPageCursors.current = [null];
		setCampaignResultsPayload({ country, campaign_id: resultsCampaign.id, batch_size: 25, page_index: 0 });
	};

	const changeResultMovementPage = (direction: 'previous' | 'next') => {
		if (!resultsCampaign || !resultsSeller || !campaignResults?.seller_detail) return;
		const detail = campaignResults.seller_detail;
		const targetPage = direction === 'next' ? detail.page_index + 1 : detail.page_index - 1;
		if (targetPage < 0 || (direction === 'next' && (!detail.has_more || !detail.next_cursor))) return;
		const cursor = direction === 'next' ? detail.next_cursor : resultPageCursors.current[targetPage] || null;
		if (direction === 'next') resultPageCursors.current[targetPage] = cursor;
		setCampaignResultsPayload({
			country,
			campaign_id: resultsCampaign.id,
			master_store_email: resultsSeller.masterEmail,
			seller_email: resultsSeller.sellerEmail,
			batch_size: detail.batch_size,
			...(cursor ? { cursor } : {}),
			page_index: targetPage
		});
	};

	const pendingCount = payoutFilter === 'requested' || payoutFilter === 'processing' ? payoutData?.total || 0 : 0;

	if (resultsCampaign) {
		return (
			<CampaignResultsView
				campaign={resultsCampaign}
				data={campaignResults}
				loading={loadingCampaignResults}
				error={campaignResultsError}
				selectedSeller={resultsSeller?.sellerEmail || ''}
				onBack={closeCampaignResults}
				onRetry={refetchCampaignResults}
				onSelectSeller={selectResultSeller}
				onCloseSeller={closeResultSeller}
				onSellerPage={changeResultMovementPage}
			/>
		);
	}

	return (
		<div className="min-h-full w-full space-y-5 p-4 md:p-6">
			{message && (
				<Dialog title="" onClose={() => setMessage(null)}>
					<p>{message}</p>
				</Dialog>
			)}
			{draft && (
				<CampaignDialog
					draft={draft}
					step={campaignStep}
					setStep={setCampaignStep}
					editing={!!editingId}
					editingCampaign={editingCampaign}
					editingId={editingId}
					campaigns={campaignData?.campaigns || []}
					country={country}
					currency={editingCampaign?.currency || campaignData?.currency || 'USD'}
					saving={saving}
					onChange={setDraft}
					onValidationError={setMessage}
					onClose={closeCampaign}
					onSave={saveCampaign}
				/>
			)}
			{campaignToStop && (
				<Dialog title="Detener campaña" onClose={() => setCampaignToStop(null)} cerrarButton={false} className="w-[min(480px,90vw)]">
					<div className="space-y-4">
						<p className="text-sm text-neutral-300">
							<strong>{campaignToStop.name}</strong> dejará de generar comisiones desde este momento. Las ganancias ya obtenidas se conservarán y
							podrán desembolsarse.
						</p>
						<div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-xs text-amber-300">
							Esta acción no se puede revertir. La campaña seguirá disponible para consulta histórica.
						</div>
						<div className="flex justify-end gap-2">
							<Button variant="secondary" className="!w-auto" onClick={() => setCampaignToStop(null)}>
								Cancelar
							</Button>
							<Button variant="destructive" className="!w-auto" loading={stopping} onClick={stopCampaign}>
								Detener campaña
							</Button>
						</div>
					</div>
				</Dialog>
			)}
			{pendingPayoutUpdate && (
				<PayoutConfirmation
					pending={pendingPayoutUpdate}
					updating={updatingRequest === pendingPayoutUpdate.request.id}
					onClose={() => setPendingPayoutUpdate(null)}
					onConfirm={(notes, receipt) => updatePayout(pendingPayoutUpdate.request, pendingPayoutUpdate.status, notes, receipt)}
				/>
			)}

			<div className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight text-neutral-100">Comisiones de vendedores</h1>
					<p className="mt-1 text-sm text-neutral-400">Configura campañas de incentivos y gestiona los desembolsos.</p>
				</div>
				{canConfigure && activeTab === 'campaigns' && (
					<Button variant="primary" className="!w-auto" disabled={!campaignData} onClick={openCreate}>
						<Plus className="size-4" /> Nueva campaña
					</Button>
				)}
			</div>

			<TabBar
				tabs={[
					...(canConfigure ? [{ id: 'campaigns', label: 'Campañas', icon: CalendarRange }] : []),
					...(canManagePayouts
						? [
								{
									id: 'payouts',
									label: 'Solicitudes',
									icon: ListChecks,
									badge: pendingCount || null
								}
							]
						: [])
				]}
				activeTab={activeTab}
				onTabChange={setActiveTab}
			/>

			{activeTab === 'campaigns' && canConfigure ? (
				<CampaignList
					campaigns={campaignData?.campaigns || []}
					loading={loadingCampaigns && !campaignData}
					error={!campaignData && campaignError}
					onRetry={refetchCampaigns}
					onView={openCampaignResults}
					onEdit={openEdit}
					onStop={setCampaignToStop}
				/>
			) : canManagePayouts ? (
				<div className="space-y-4">
					<PayoutRequests
						requests={payoutData?.requests || []}
						total={payoutData?.total || 0}
						filter={payoutFilter}
						loading={loadingPayouts && !payoutData}
						error={payoutError}
						updating={updatingRequest}
						onFilter={changePayoutFilter}
						onRetry={refetchPayouts}
						onUpdate={(request, status) => setPendingPayoutUpdate({ request, status })}
						onDownloadReceipt={downloadReceipt}
					/>
					{!loadingPayouts && !payoutError && (payoutData?.total || 0) > (payoutData?.batch_size || 100) && (
						<div className="flex items-center justify-between gap-3 border-t border-neutral-800 pt-4">
							<p className="text-xs tabular-nums text-neutral-500">
								{payoutOffset + 1}–{Math.min(payoutOffset + (payoutData?.batch_size || 100), payoutData?.total || 0)} de {payoutData?.total || 0}
							</p>
							<div className="flex gap-2">
								<Button variant="secondary" className="!w-auto" disabled={payoutPaging || payoutOffset === 0} onClick={previousPayoutPage}>
									Anterior
								</Button>
								<Button
									variant="secondary"
									className="!w-auto"
									disabled={payoutPaging || !payoutData?.has_more}
									onClick={nextPayoutPage}
								>
									Siguiente
								</Button>
							</div>
						</div>
					)}
				</div>
			) : null}
		</div>
	);
}

function CampaignResultsView({
	campaign,
	data,
	loading,
	error,
	selectedSeller,
	onBack,
	onRetry,
	onSelectSeller,
	onCloseSeller,
	onSellerPage
}: {
	campaign: SellerCommissionCampaign;
	data: SellerCommissionCampaignResults | null;
	loading: boolean;
	error: unknown;
	selectedSeller: string;
	onBack: () => void;
	onRetry: () => void;
	onSelectSeller: (masterEmail: string, sellerEmail: string) => void;
	onCloseSeller: () => void;
	onSellerPage: (direction: 'previous' | 'next') => void;
}) {
	const currentData = data?.campaign.id === campaign.id ? data : null;
	const displayedCampaign = currentData?.campaign || campaign;
	const sellerDetail = selectedSeller ? currentData?.seller_detail : null;
	const waitingForSeller = !!selectedSeller && sellerDetail?.email !== selectedSeller;
	return (
		<div className="min-h-full w-full space-y-5 p-4 md:p-6">
			<div className="flex flex-col items-start gap-3 sm:flex-row sm:justify-between">
				<div className="flex min-w-0 flex-col items-start gap-3 sm:flex-row">
					<Button variant="secondary" className="!w-auto" onClick={onBack}>
						<ArrowLeft className="size-4" /> Campañas
					</Button>
					<div className="min-w-0">
						<div className="flex flex-wrap items-center gap-2">
							<h1 className="truncate text-2xl font-semibold tracking-tight text-neutral-100">{displayedCampaign.name}</h1>
							<span className={cn('rounded-md px-2 py-1 text-xs', campaignStatusClass[displayedCampaign.status])}>
								{campaignStatus[displayedCampaign.status]}
							</span>
						</div>
						<p className="mt-1 text-sm text-neutral-400">Resultados consolidados de la campaña por tienda, sucursal y vendedor.</p>
					</div>
				</div>
				<Button variant="secondary" className="!w-auto" loading={loading} onClick={onRetry}>
					<RefreshCw className="size-4" /> Actualizar
				</Button>
			</div>

			{loading && (!currentData || waitingForSeller) ? (
				<AdminListSkeleton />
			) : error && (!currentData || waitingForSeller) ? (
				<InlineError message="No se pudieron cargar los resultados de la campaña." onRetry={onRetry} />
			) : currentData ? (
				sellerDetail ? (
					<div className="space-y-4">
						<Button variant="secondary" className="!w-auto" onClick={onCloseSeller}>
							<ArrowLeft className="size-4" /> Volver al consolidado
						</Button>
						<SellerDetail
							detail={sellerDetail}
							currency={currentData.currency}
							payoutRecipient={currentData.campaign.payout_recipient}
							onPage={onSellerPage}
						/>
					</div>
				) : (
					<>
						<div className="grid overflow-hidden rounded-xl border border-neutral-800 bg-neutral-925/50 sm:grid-cols-2 lg:grid-cols-4">
							<CampaignField label="Ventas comisionadas" icon={Check}>
								<strong className="tabular-nums">{currentData.stats.sales_count}</strong>
							</CampaignField>
							<CampaignField label="Comisiones generadas" icon={CircleDollarSign}>
								<strong className="tabular-nums text-emerald-300 light:text-emerald-700">+{money(currentData.currency, currentData.stats.gross_commissions)}</strong>
							</CampaignField>
							<CampaignField label="Reversos" icon={XCircle}>
								<strong className="tabular-nums text-amber-300 light:text-amber-700">−{money(currentData.currency, currentData.stats.reversed_commissions)}</strong>
							</CampaignField>
							<CampaignField label="Total neto" icon={BarChart3}>
								<strong className="tabular-nums text-primary-300 light:text-primary-700">{money(currentData.currency, currentData.total_commissions)}</strong>
							</CampaignField>
						</div>
						<CampaignEarningSetup campaign={currentData.campaign} role="master" />
						<section className="space-y-3">
							<div className="flex items-center justify-between gap-3">
								<h2 className="font-medium text-neutral-100">Resultados por tienda master</h2>
								<span className="text-xs tabular-nums text-neutral-500">
									{currentData.masters.length} {currentData.masters.length === 1 ? 'tienda' : 'tiendas'}
								</span>
							</div>
							{currentData.masters.map((master) => (
								<details
									key={master.email}
									open={currentData.masters.length === 1}
									className="group overflow-hidden rounded-xl border border-neutral-800 bg-neutral-925/50"
								>
									<summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-4">
										<div className="flex min-w-0 items-center gap-3">
											<div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-neutral-900 text-primary-300 light:text-primary-700">
												<Building2 className="size-4" />
											</div>
											<div className="min-w-0">
												<p className="truncate font-medium text-neutral-100">{master.name}</p>
												<p className="truncate text-xs text-neutral-500">{master.email}</p>
											</div>
										</div>
										<div className="flex items-center gap-3">
											<strong className="tabular-nums text-neutral-100">{money(currentData.currency, master.total_commissions)}</strong>
											<ChevronDown className="size-4 text-neutral-500 transition-transform group-open:rotate-180" />
										</div>
									</summary>
									<div className="border-t border-neutral-800 p-4">
										<StoreDashboard
											data={{ role: 'master', currency: currentData.currency, branches: master.branches }}
											selectedSeller={selectedSeller}
											onSelectSeller={(sellerEmail) => onSelectSeller(master.email, sellerEmail)}
										/>
									</div>
								</details>
							))}
							{!currentData.masters.length && (
								<EmptyState
									icon={Building2}
									title="Esta campaña todavía no tiene tiendas con resultados"
									description="Los targets aparecerán aquí aunque su total sea cero."
								/>
							)}
						</section>
					</>
				)
			) : null}
		</div>
	);
}

function CampaignList({
	campaigns,
	loading,
	error,
	onRetry,
	onView,
	onEdit,
	onStop
}: {
	campaigns: SellerCommissionCampaign[];
	loading: boolean;
	error: unknown;
	onRetry: () => void;
	onView: (campaign: SellerCommissionCampaign) => void;
	onEdit: (campaign: SellerCommissionCampaign) => void;
	onStop: (campaign: SellerCommissionCampaign) => void;
}) {
	const [filter, setFilter] = useState<CampaignFilter>('all');
	const [query, setQuery] = useState('');
	const counts = useMemo(
		() => ({
			all: campaigns.length,
			active: campaigns.filter((campaign) => campaign.status === 'active').length,
			upcoming: campaigns.filter((campaign) => campaign.status === 'upcoming').length,
			ended: campaigns.filter((campaign) => campaign.status === 'ended').length,
			stopped: campaigns.filter((campaign) => campaign.status === 'stopped').length
		}),
		[campaigns]
	);
	const visible = useMemo(
		() =>
			campaigns
				.filter((campaign) => filter === 'all' || campaign.status === filter)
				.filter((campaign) =>
					`${campaign.name} ${campaign.targets.map((target) => target.label).join(' ')}`.toLowerCase().includes(query.trim().toLowerCase())
				)
				.sort((a, b) => campaignOrder(a.status) - campaignOrder(b.status) || new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime()),
		[campaigns, filter, query]
	);

	if (loading) return <AdminListSkeleton />;
	if (error) return <InlineError message="No se pudieron cargar las campañas." onRetry={onRetry} />;
	return (
		<div className="space-y-4">
			<div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
				<div className="flex flex-wrap gap-2">
					{(
						[
							['all', 'Todas'],
							['active', 'Activas'],
							['upcoming', 'Programadas'],
							['ended', 'Finalizadas'],
							['stopped', 'Detenidas']
						] as [CampaignFilter, string][]
					).map(([value, label]) => (
						<button
							key={value}
							type="button"
							onClick={() => setFilter(value)}
							className={cn(
								'rounded-lg px-3 py-2 text-xs font-medium transition-colors',
								filter === value ? 'bg-neutral-800 text-neutral-100' : 'text-neutral-400 hover:bg-neutral-900/50 hover:text-neutral-200'
							)}
						>
							{label} <span className="ml-1 tabular-nums text-neutral-500">{counts[value]}</span>
						</button>
					))}
				</div>
				<label className="flex min-w-[250px] items-center gap-2 rounded-lg border border-neutral-800 bg-neutral-925/50 px-3 py-2 text-sm focus-within:border-primary-500/50">
					<Search className="size-4 text-neutral-500" />
					<input
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						placeholder="Buscar campaña o tienda..."
						className="w-full bg-transparent text-neutral-200 outline-none placeholder:text-neutral-600"
					/>
				</label>
			</div>
			{visible.length ? (
				visible.map((campaign) => <CampaignCard key={campaign.id} campaign={campaign} onView={onView} onEdit={onEdit} onStop={onStop} />)
			) : (
				<EmptyState icon={CalendarRange} title="No hay campañas con estos filtros" description="Cambia el estado seleccionado o limpia la búsqueda." />
			)}
			<p className="flex items-start gap-2 text-xs text-neutral-500">
				<AlertCircle className="mt-0.5 size-3.5 shrink-0" /> Una tienda o sucursal no puede participar en campañas solapadas. Una campaña BAU ocupa el alcance hasta que se detenga; después del inicio solo se puede extender o eliminar el cierre y agregar alcance.
			</p>
		</div>
	);
}

function CampaignCard({
	campaign,
	onView,
	onEdit,
	onStop
}: {
	campaign: SellerCommissionCampaign;
	onView: (campaign: SellerCommissionCampaign) => void;
	onEdit: (campaign: SellerCommissionCampaign) => void;
	onStop: (campaign: SellerCommissionCampaign) => void;
}) {
	const active = campaign.status === 'active';
	const progress = campaign.ends_at ? campaignProgress(campaign) : 100;
	const configuredTiers = campaign.plan.mode === 'sales_volume_tiers' ? campaign.plan.volume_tiers || [] : campaign.plan.tiers;
	const commissionRange = configuredTiers.length
		? `${money(campaign.currency, Math.min(...configuredTiers.map((tier) => tier.amount)))}–${money(campaign.currency, Math.max(...configuredTiers.map((tier) => tier.amount)))}`
		: '';
	return (
		<article
			className={cn(
				'overflow-hidden rounded-xl border bg-neutral-925/50',
				active ? 'border-primary-500/50 ring-1 ring-primary-500/20' : 'border-neutral-800'
			)}
		>
			<header className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
				<div className="min-w-0">
					<div className="flex flex-wrap items-center gap-2">
						<h2 className="font-semibold text-neutral-100">{campaign.name}</h2>
						<span className={cn('rounded-md px-2 py-1 text-xs', campaignStatusClass[campaign.status])}>{campaignStatus[campaign.status]}</span>
						{!campaign.ends_at && <span className="rounded-md bg-primary-500/10 px-2 py-1 text-xs text-primary-300">BAU</span>}
					</div>
					{!active && (
						<p className="mt-2 text-xs tabular-nums text-neutral-500">
							{campaignVigencyLabel(campaign.starts_at, campaign.ends_at, true)}
						</p>
					)}
				</div>
				<div className="flex shrink-0 flex-wrap gap-2">
					<Button variant="secondary" className="!w-auto" onClick={() => onView(campaign)}>
						<BarChart3 className="size-4" /> Ver resultados
					</Button>
					{canEditCampaign(campaign) && (
						<Button variant="secondary" className="!w-auto" onClick={() => onEdit(campaign)}>
							<Pencil className="size-4" /> {campaign.status === 'ended' ? 'Reactivar' : 'Editar'}
						</Button>
					)}
					{(campaign.status === 'upcoming' || campaign.status === 'active') && (
						<Button variant="secondary" className="!w-auto text-red-400" onClick={() => onStop(campaign)}>
							<CircleStop className="size-4" /> Detener
						</Button>
					)}
				</div>
			</header>
			{active && (
				<div className="border-t border-neutral-800 px-4 py-3">
					{campaign.ends_at ? (
						<div className="flex items-center gap-3 text-xs tabular-nums text-neutral-500">
							<span>{formatDateTime(campaign.starts_at)}</span>
							<div className="h-1 flex-1 overflow-hidden rounded bg-neutral-800">
								<div className="h-full bg-primary-500" style={{ width: `${progress}%` }} />
							</div>
							<span>{formatDateTime(campaign.ends_at)}</span>
							<strong className="text-emerald-300">Termina {relativeEnd(campaign.ends_at)}</strong>
						</div>
					) : (
						<div className="flex flex-wrap items-center gap-2 text-xs text-emerald-300">
							<InfinityIcon className="size-4" />
							<strong>Campaña BAU sin fecha de cierre</strong>
							<span className="text-neutral-500">· activa hasta que se detenga</span>
						</div>
					)}
				</div>
			)}
			<div className="grid border-t border-neutral-800 sm:grid-cols-2 lg:grid-cols-[1.15fr_.8fr_.8fr_.8fr_1.35fr]">
				<CampaignField label="Regla de comisión" icon={CircleDollarSign}>
					<strong>
						{campaign.plan.mode === 'fixed_per_sale'
							? 'Monto fijo'
							: campaign.plan.mode === 'sales_volume_tiers'
								? `${configuredTiers.length} rangos por ventas`
								: `${configuredTiers.length} rangos por precio`}
					</strong>
					{campaign.plan.mode === 'fixed_per_sale' ? ` · ${money(campaign.currency, campaign.plan.fixed_amount)} por venta` : ` · ${commissionRange}`}
				</CampaignField>
				<CampaignField label="Productos" icon={campaign.product_categories.includes('phones') || campaign.product_categories.includes('iphones') ? Smartphone : Package}>
					<strong>{productCategoriesLabel(campaign.product_categories)}</strong>
				</CampaignField>
				<CampaignField label="Quién cobra" icon={campaign.payout_recipient === 'master' ? Store : UserRound}>
					<strong>{campaign.payout_recipient === 'master' ? 'Tienda master' : 'Cada vendedor'}</strong>
				</CampaignField>
				<CampaignField label="Mínimo" icon={WalletCards}>
					<strong className="tabular-nums">{money(campaign.currency, campaign.min_payout_amount)}</strong>
				</CampaignField>
				<CampaignField label={`Alcance · ${campaign.targets.length}`} icon={Target}>
					<div className="flex flex-wrap gap-1.5">
						{campaign.targets.slice(0, 2).map((target) => (
							<span key={target.key} className="rounded-md bg-neutral-900 px-2 py-1 text-xs text-neutral-300">
								{target.label}
							</span>
						))}
						{campaign.targets.length > 2 && (
							<span className="rounded-md bg-neutral-900 px-2 py-1 text-xs text-neutral-500">+{campaign.targets.length - 2}</span>
						)}
					</div>
				</CampaignField>
			</div>
		</article>
	);
}

function CampaignField({ label, icon: Icon, children }: { label: string; icon: typeof Store; children: React.ReactNode }) {
	return (
		<div className="border-b border-neutral-800 p-4 last:border-b-0 sm:border-r lg:border-b-0 lg:last:border-r-0">
			<p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">{label}</p>
			<div className="mt-2 flex items-center gap-2 text-sm text-neutral-400">
				<Icon className="size-4 shrink-0" />
				<div>{children}</div>
			</div>
		</div>
	);
}

function CampaignDialog({
	draft,
	step,
	setStep,
	editing,
	editingCampaign,
	editingId,
	campaigns,
	country,
	currency,
	saving,
	onChange,
	onValidationError,
	onClose,
	onSave
}: {
	draft: SellerCommissionCampaignInput;
	step: CampaignStep;
	setStep: (step: CampaignStep) => void;
	editing: boolean;
	editingCampaign: SellerCommissionCampaign | null;
	editingId: string | null;
	campaigns: SellerCommissionCampaign[];
	country: string;
	currency: string;
	saving: boolean;
	onChange: (draft: SellerCommissionCampaignInput) => void;
	onValidationError: (message: string) => void;
	onClose: () => void;
	onSave: () => void;
}) {
	const restrictedEdit = isRestrictedCampaignEdit(editingCampaign);
	const existingTargetKeys = editingCampaign?.target_keys || [];
	const [knownTargets, setKnownTargets] = useState(
		() => new Map((editingCampaign?.targets || []).map((target) => [target.key, target]))
	);
	const [scopeVisited, setScopeVisited] = useState(false);
	const contentRef = useRef<HTMLDivElement>(null);
	const selectedTargets = useMemo(
		() => draft.target_keys.map((key) => knownTargets.get(key)).filter(Boolean) as SellerCommissionTarget[],
		[draft.target_keys, knownTargets]
	);
	const overlap = restrictedEdit ? null : findOverlappingCampaign(draft, campaigns, editingId);
	const stepError = validateCampaignStep(draft, step, editingCampaign);

	useEffect(() => {
		contentRef.current?.scrollTo({ top: 0 });
	}, [step]);

	const addTarget = (target: SellerCommissionTarget) => {
		const masterKey = `master:${target.master_store_email}`;
		if (target.type === 'branch' && draft.target_keys.includes(masterKey)) return;
		const next =
			target.type === 'master'
				? draft.target_keys.filter((item) => !item.startsWith(`branch:${target.master_store_email}:`))
				: [...draft.target_keys];
		setKnownTargets((current) => new Map(current).set(target.key, target));
		onChange({ ...draft, target_keys: [...next, target.key] });
	};

	const updateTier = (index: number, field: keyof SellerCommissionTier, value: string) => {
		const tiers = [...draft.plan.tiers];
		tiers[index] = {
			...tiers[index],
			[field]: field === 'max_price' && value === '' ? null : parseDecimal(value)
		};
		onChange({ ...draft, plan: { ...draft.plan, tiers } });
	};

	const updateVolumeTier = (index: number, field: keyof SellerCommissionVolumeTier, value: number | null) => {
		const volume_tiers = [...(draft.plan.volume_tiers || [])];
		volume_tiers[index] = { ...volume_tiers[index], [field]: value } as SellerCommissionVolumeTier;
		if (field === 'max_sales' && value !== null && volume_tiers[index + 1]) volume_tiers[index + 1].min_sales = value + 1;
		onChange({ ...draft, plan: { ...draft.plan, volume_tiers } });
	};

	const next = () => {
		if (stepError) {
			onValidationError(stepError);
			return;
		}
		if (step === 1) setScopeVisited(true);
		setStep(Math.min(4, step + 1) as CampaignStep);
	};

	return (
		<Dialog title="" onClose={onClose} className="!w-[min(1080px,96vw)] !max-w-none !gap-0 !overflow-hidden !p-0" cerrarButton={false} noContentWrapper>
			<div className="border-b border-neutral-800 p-5 md:px-7">
				<h2 className="text-xl font-semibold tracking-tight text-neutral-100">{editing ? 'Editar campaña' : 'Nueva campaña'}</h2>
				<p className="mt-1 text-sm text-neutral-500">{draft.name || 'Define la campaña antes de publicarla'}</p>
				<CampaignSteps step={step} />
			</div>
			<div ref={contentRef} className="grid max-h-[68vh] overflow-y-auto lg:grid-cols-[minmax(0,1fr)_310px]">
				<div className="p-5 md:p-7">
					{step === 1 && <InformationStep draft={draft} editingCampaign={editingCampaign} onChange={onChange} />}
					{scopeVisited && (
						<div className={step === 2 ? undefined : 'hidden'}>
							<ScopeStep
								draft={draft}
								country={country}
								selectedTargets={selectedTargets}
								addTarget={addTarget}
								existingTargetKeys={existingTargetKeys}
								restrictedEdit={restrictedEdit}
								onChange={onChange}
							/>
						</div>
					)}
					{step === 3 && (
						<CommissionStep
							draft={draft}
							currency={currency}
							disabled={restrictedEdit}
							updateTier={updateTier}
							updateVolumeTier={updateVolumeTier}
							onChange={onChange}
						/>
					)}
					{step === 4 && (
						<ReviewStep
							draft={draft}
							currency={currency}
							selectedTargets={selectedTargets}
							overlap={overlap}
							restrictedEdit={restrictedEdit}
						/>
					)}
				</div>
				<LiveSummary draft={draft} currency={currency} selectedTargets={selectedTargets} overlap={overlap} />
			</div>
			<footer className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-800 p-4 md:px-7">
				<div className="flex items-center gap-3">
					{step > 1 && (
						<Button variant="secondary" className="!w-auto" onClick={() => setStep((step - 1) as CampaignStep)}>
							<ArrowLeft className="size-4" /> Atrás
						</Button>
					)}
					<span className="text-xs text-neutral-500">Paso {step} de 4</span>
				</div>
				<div className="flex items-center gap-2">
					<Button variant="text" className="!w-auto" onClick={onClose}>
						Cancelar
					</Button>
					{step < 4 ? (
						<Button variant="primary" className="!w-auto" onClick={next}>
							Continuar <ArrowRight className="size-4" />
						</Button>
					) : (
						<Button variant="primary" className="!w-auto" loading={saving} disabled={!!overlap} onClick={onSave}>
							<Check className="size-4" /> {editing ? 'Guardar cambios' : 'Crear campaña'}
						</Button>
					)}
				</div>
			</footer>
		</Dialog>
	);
}

function CampaignSteps({ step }: { step: CampaignStep }) {
	const steps = ['Información', 'Alcance', 'Comisión y desembolso', 'Revisión'];
	const stepsRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const container = stepsRef.current;
		const activeStep = container?.children.item(step - 1) as HTMLElement | null;
		if (!container || !activeStep) return;
		container.scrollTo({
			left: activeStep.offsetLeft - (container.clientWidth - activeStep.clientWidth) / 2
		});
	}, [step]);

	return (
		<div ref={stepsRef} className="mt-5 flex overflow-x-auto pb-1">
			{steps.map((label, index) => {
				const number = index + 1;
				const complete = number < step;
				const active = number === step;
				return (
					<div key={label} className="flex min-w-max flex-1 items-center">
						<div
							className={cn(
								'flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
								complete
									? 'border-primary-500 bg-primary-500/10 text-primary-300'
									: active
										? 'border-primary-500 bg-primary-500 text-black'
										: 'border-neutral-700 text-neutral-500'
							)}
						>
							{complete ? <Check className="size-4" /> : number}
						</div>
						<span className={cn('ml-2 text-sm', active ? 'font-medium text-neutral-100' : 'text-neutral-500')}>{label}</span>
						{number < 4 && <div className="mx-4 h-px min-w-8 flex-1 bg-neutral-800" />}
					</div>
				);
			})}
		</div>
	);
}

function InformationStep({
	draft,
	editingCampaign,
	onChange
}: {
	draft: SellerCommissionCampaignInput;
	editingCampaign: SellerCommissionCampaign | null;
	onChange: (draft: SellerCommissionCampaignInput) => void;
}) {
	const restrictedEdit = isRestrictedCampaignEdit(editingCampaign);
	const withoutExpiration = draft.ends_at === null;
	const cannotShortenBau = restrictedEdit && editingCampaign?.ends_at === null;
	return (
		<div className="space-y-5">
			<div>
				<h3 className="font-medium text-neutral-100">Información de la campaña</h3>
				<p className="mt-1 text-sm text-neutral-500">
					{restrictedEdit
						? 'Puedes extender o eliminar el cierre; el nombre y el inicio ya son inmutables.'
						: 'El inicio debe estar en el futuro. El cierre puede ser una fecha o mantenerse abierto como BAU.'}
				</p>
			</div>
			<Input
				floatingLabel="Nombre de la campaña"
				placeholder="Comisiones regreso a clases"
				value={draft.name}
				disabled={restrictedEdit}
				onChange={(name) => onChange({ ...draft, name })}
			/>
			<div className={cn('grid gap-5', !withoutExpiration && 'sm:grid-cols-2')}>
				<DateTimePicker
					floatingLabel="Inicio"
					defaultTimestamp={draft.starts_at ? new Date(draft.starts_at).getTime() / 1000 : undefined}
					emitInitialValue={false}
					disabled={restrictedEdit}
					onDateTimeChange={(starts_at) => onChange({ ...draft, starts_at: starts_at || '' })}
					largeCalendar
				/>
				{!withoutExpiration && (
					<DateTimePicker
						floatingLabel="Cierre"
						defaultTimestamp={draft.ends_at ? new Date(draft.ends_at).getTime() / 1000 : undefined}
						emitInitialValue={false}
						onDateTimeChange={(ends_at) => onChange({ ...draft, ends_at: ends_at || '' })}
						largeCalendar
					/>
				)}
			</div>
			<div className="flex items-center justify-between gap-4 rounded-lg border border-neutral-800 bg-neutral-950/30 p-4">
				<div>
					<p className="flex items-center gap-2 text-sm font-medium text-neutral-200">
						<InfinityIcon className="size-4 text-primary-300" /> Sin fecha de expiración
					</p>
					<p className="mt-1 text-xs text-neutral-500">La campaña permanece activa hasta que un administrador la detenga.</p>
				</div>
				<Switch
					checked={withoutExpiration}
					disabled={cannotShortenBau}
					onCheckedChange={(checked) =>
						onChange({
							...draft,
							ends_at: checked ? null : editingCampaign?.ends_at || defaultCampaignEnd(draft.starts_at)
						})
					}
				/>
			</div>
			<div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 text-xs text-blue-300">
				{restrictedEdit
					? 'Una campaña con cierre puede extenderse o pasar a BAU. Una campaña BAU iniciada no puede volver a tener vencimiento. Las reactivaciones no cubren el lapso cerrado.'
					: 'Una campaña BAU bloquea otra campaña para el mismo alcance hasta que se detenga. Las campañas con cierre pueden ser adyacentes, pero no solaparse.'}
			</div>
		</div>
	);
}

function ScopeStep({
	draft,
	country,
	selectedTargets,
	addTarget,
	existingTargetKeys,
	restrictedEdit,
	onChange
}: {
	draft: SellerCommissionCampaignInput;
	country: string;
	selectedTargets: SellerCommissionTarget[];
	addTarget: (target: SellerCommissionTarget) => void;
	existingTargetKeys: string[];
	restrictedEdit: boolean;
	onChange: (draft: SellerCommissionCampaignInput) => void;
}) {
	const [showSelector, setShowSelector] = useState(selectedTargets.length === 0);
	const selectorVisible = showSelector || selectedTargets.length === 0;
	return (
		<div className="flex flex-col gap-5">
			<div>
				<h3 className="font-medium text-neutral-100">Tiendas y sucursales</h3>
				<p className="mt-1 text-sm text-neutral-500">
					{restrictedEdit
						? 'Puedes agregar tiendas o sucursales; empezarán a generar comisión desde que guardes el cambio.'
						: 'Seleccionar una tienda completa incluye todas sus sucursales.'}
				</p>
			</div>
			{selectorVisible && (
				<div className={selectedTargets.length ? 'order-2 space-y-2' : 'order-1'}>
					{selectedTargets.length > 0 && <p className="text-sm font-medium text-neutral-200">Agregar alcance</p>}
					<SellerCommissionTargetSelector country={country} selectedKeys={draft.target_keys} onSelect={addTarget} />
				</div>
			)}
			<div className={selectedTargets.length ? 'order-1 space-y-2' : 'order-2 space-y-2'}>
				{selectedTargets.length > 0 && (
					<div className="flex flex-wrap items-center justify-between gap-3">
						<p className="text-sm font-medium text-neutral-200">Alcance seleccionado</p>
						<Button variant="secondary" className="!w-auto" onClick={() => setShowSelector((visible) => !visible)}>
							{selectorVisible ? <X className="size-4" /> : <Plus className="size-4" />}
							{selectorVisible ? 'Cerrar buscador' : 'Agregar otra tienda o sucursal'}
						</Button>
					</div>
				)}
				{selectedTargets.map((target) => {
					const lockedTarget = restrictedEdit && existingTargetKeys.includes(target.key);
					return (
					<div key={target.key} className="flex items-center justify-between gap-3 rounded-xl border border-primary-500/30 bg-primary-500/5 p-3">
						<div className="flex min-w-0 items-center gap-3">
							<div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-neutral-900 text-primary-300">
								{target.type === 'master' ? <Store className="size-4" /> : <Building2 className="size-4" />}
							</div>
							<div className="min-w-0">
								<p className="truncate text-sm font-medium text-neutral-100">{target.label}</p>
								<p className="truncate text-xs text-neutral-500">
									{target.type === 'master' ? target.master_store_email : target.branch_store_email}
								</p>
							</div>
						</div>
						{lockedTarget ? (
							<span className="inline-flex items-center gap-1 text-xs text-primary-300">
								<Check className="size-3.5" /> Incluida desde el inicio
							</span>
						) : (
							<Button
								variant="secondary"
								className="!w-auto !p-2"
								aria-label="Remover alcance"
								onClick={() =>
									onChange({
										...draft,
										target_keys: draft.target_keys.filter((key) => key !== target.key)
									})
								}
							>
								<X className="size-4" />
							</Button>
						)}
					</div>
					);
				})}
				{!selectedTargets.length && (
					<EmptyState
						icon={Target}
						title="Selecciona al menos una tienda o sucursal"
						description="El alcance determina qué ventas pueden generar comisión."
						compact
					/>
				)}
			</div>
		</div>
	);
}

function CommissionStep({
	draft,
	currency,
	disabled,
	updateTier,
	updateVolumeTier,
	onChange
}: {
	draft: SellerCommissionCampaignInput;
	currency: string;
	disabled: boolean;
	updateTier: (index: number, field: keyof SellerCommissionTier, value: string) => void;
	updateVolumeTier: (index: number, field: keyof SellerCommissionVolumeTier, value: number | null) => void;
	onChange: (draft: SellerCommissionCampaignInput) => void;
}) {
	const example = draft.plan.mode === 'sales_volume_tiers' ? volumeTierExample(draft, currency) : tierExample(draft, currency);
	const volumeTiers = draft.plan.volume_tiers || [];
	const lastVolumeTier = volumeTiers[volumeTiers.length - 1];
	const canAddVolumeTier = !lastVolumeTier || lastVolumeTier.max_sales !== null;
	const nextVolumeMinimum = lastVolumeTier?.max_sales ? lastVolumeTier.max_sales + 1 : 1;
	const removeVolumeTier = (index: number) => {
		const volume_tiers = volumeTiers.filter((_, tierIndex) => tierIndex !== index).map((tier) => ({ ...tier }));
		volume_tiers.forEach((tier, tierIndex) => {
			if (tierIndex === 0) tier.min_sales = 1;
			else if (volume_tiers[tierIndex - 1].max_sales !== null) tier.min_sales = (volume_tiers[tierIndex - 1].max_sales || 0) + 1;
		});
		onChange({ ...draft, plan: { ...draft.plan, volume_tiers } });
	};
	const toggleProductCategory = (category: SellerCommissionProductCategory) => {
		const selected = draft.product_categories.includes(category);
		onChange({
			...draft,
			product_categories: selected ? draft.product_categories.filter((item) => item !== category) : [...draft.product_categories, category]
		});
	};
	return (
		<div className="space-y-6">
			{disabled && (
				<div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-xs text-amber-300">
					La comisión, los productos, quién cobra y el mínimo quedan inmutables después del inicio.
				</div>
			)}
			<fieldset disabled={disabled} className="space-y-6 disabled:opacity-60">
				<section className="space-y-3">
				<div>
					<h3 className="font-medium text-neutral-100">Productos incluidos</h3>
					<p className="mt-1 text-sm text-neutral-500">Elige qué ventas generan comisión dentro de la campaña.</p>
				</div>
				<div className="grid gap-3 sm:grid-cols-3">
					<SelectionCard
						selected={draft.product_categories.includes('phones')}
						icon={Smartphone}
						title="Celulares"
						description="Incluye solo equipos Android y tablets."
						onClick={() => toggleProductCategory('phones')}
					/>
					<SelectionCard
						selected={draft.product_categories.includes('iphones')}
						icon={Smartphone}
						title="iPhones"
						description="Incluye solo equipos registrados como iPhone."
						onClick={() => toggleProductCategory('iphones')}
					/>
					<SelectionCard
						selected={draft.product_categories.includes('other')}
						icon={Package}
						title="Otros"
						description="Incluye laptops y productos físicos registrados como otros."
						onClick={() => toggleProductCategory('other')}
					/>
				</div>
			</section>
			<section className="space-y-3">
				<div>
					<h3 className="font-medium text-neutral-100">¿Quién solicita el desembolso?</h3>
					<p className="mt-1 text-sm text-neutral-500">El saldo, el mínimo y las solicitudes quedarán aislados en esta campaña.</p>
				</div>
				<div className="grid gap-3 sm:grid-cols-2">
					<SelectionCard
						selected={draft.payout_recipient === 'seller'}
						icon={UserRound}
						title="Cada vendedor"
						description="Cada vendedor acumula su saldo, registra su cuenta y solicita su propio desembolso."
						onClick={() => onChange({ ...draft, payout_recipient: 'seller' })}
					/>
					<SelectionCard
						selected={draft.payout_recipient === 'master'}
						icon={Store}
						title="La tienda master"
						description="La master solicita el total consolidado y lo distribuye internamente."
						onClick={() => onChange({ ...draft, payout_recipient: 'master' })}
					/>
				</div>
			</section>
			<section className="space-y-4">
				<div>
					<h3 className="font-medium text-neutral-100">Modalidad de comisión</h3>
				</div>
				<div className="inline-flex flex-wrap rounded-lg border border-neutral-800 bg-neutral-950/30 p-1">
					<button
						type="button"
						onClick={() =>
							onChange({
								...draft,
								plan: { ...draft.plan, mode: 'fixed_per_sale' }
							})
						}
						className={cn(
							'rounded-md px-4 py-2 text-sm',
							draft.plan.mode === 'fixed_per_sale' ? 'bg-neutral-800 text-neutral-100' : 'text-neutral-500'
						)}
					>
						Monto fijo por venta
					</button>
					<button
						type="button"
						onClick={() =>
							onChange({
								...draft,
								plan: { ...draft.plan, mode: 'price_tiers' }
							})
						}
						className={cn(
							'rounded-md px-4 py-2 text-sm',
							draft.plan.mode === 'price_tiers' ? 'bg-neutral-800 text-neutral-100' : 'text-neutral-500'
						)}
					>
						Rangos por precio
					</button>
					<button
						type="button"
						onClick={() =>
							onChange({
								...draft,
								plan: {
									...draft.plan,
									mode: 'sales_volume_tiers',
									volume_tiers: volumeTiers.length ? volumeTiers : [{ min_sales: 1, max_sales: null, amount: 0 }]
								}
							})
						}
						className={cn(
							'rounded-md px-4 py-2 text-sm',
							draft.plan.mode === 'sales_volume_tiers' ? 'bg-neutral-800 text-neutral-100' : 'text-neutral-500'
						)}
					>
						Rangos por ventas
					</button>
				</div>
				{draft.plan.mode === 'fixed_per_sale' ? (
					<DecimalInput
						floatingLabel="Comisión por venta"
						value={draft.plan.fixed_amount}
						onChange={(fixed_amount) =>
							onChange({
								...draft,
								plan: { ...draft.plan, fixed_amount }
							})
						}
						containerClassName="max-w-sm"
					/>
				) : draft.plan.mode === 'price_tiers' ? (
					<div className="space-y-3">
						<div className="hidden grid-cols-[1fr_1fr_1fr_auto] gap-3 px-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-500 sm:grid">
							<span>Desde ({currency})</span>
							<span>Hasta ({currency})</span>
							<span>Comisión ({currency})</span>
							<span />
						</div>
						{draft.plan.tiers.map((tier, index) => (
							<div key={index} className="grid gap-3 rounded-xl border border-neutral-800 bg-neutral-925/50 p-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
								<DecimalInput
									floatingLabel="Desde"
									value={tier.min_price}
									onChange={(value) => updateTier(index, 'min_price', String(value))}
								/>
								<DecimalInput
									floatingLabel="Hasta"
									placeholder="Sin límite"
									value={tier.max_price}
									onChange={(value, raw) => updateTier(index, 'max_price', raw ? String(value) : '')}
								/>
								<DecimalInput
									floatingLabel="Comisión"
									value={tier.amount}
									onChange={(value) => updateTier(index, 'amount', String(value))}
								/>
								<Button
									variant="secondary"
									className="!w-auto self-end text-red-400"
									aria-label="Eliminar rango"
									onClick={() =>
										onChange({
											...draft,
											plan: {
												...draft.plan,
												tiers: draft.plan.tiers.filter((_, tierIndex) => tierIndex !== index)
											}
										})
									}
								>
									<Trash2 className="size-4" />
								</Button>
							</div>
						))}
						<Button
							variant="secondary"
							className="!w-auto"
							onClick={() =>
								onChange({
									...draft,
									plan: {
										...draft.plan,
										tiers: [...draft.plan.tiers, { min_price: 0, max_price: null, amount: 0 }]
									}
								})
							}
						>
							<Plus className="size-4" /> Agregar rango
						</Button>
						{example && (
							<div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 text-sm text-blue-200">
								<CircleDollarSign className="mr-2 inline size-4" />
								{example}
							</div>
						)}
					</div>
				) : (
					<div className="space-y-3">
						<div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 text-xs leading-relaxed text-blue-200">
							Cada vendedor avanza por sus propios rangos durante toda la campaña. Solo las ventas nuevas usan el monto del rango alcanzado; las anteriores no se recalculan.
						</div>
						<div className="hidden grid-cols-[1fr_1fr_1fr_auto] gap-3 px-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-500 sm:grid">
							<span>Desde venta</span>
							<span>Hasta venta</span>
							<span>Comisión ({currency})</span>
							<span />
						</div>
						{volumeTiers.map((tier, index) => (
							<div key={index} className="grid gap-3 rounded-xl border border-neutral-800 bg-neutral-925/50 p-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
								<IntegerInput floatingLabel="Desde venta" value={tier.min_sales} disabled onChange={() => undefined} />
								<IntegerInput floatingLabel="Hasta venta" placeholder="Sin límite" value={tier.max_sales} allowEmpty onChange={(value) => updateVolumeTier(index, 'max_sales', value)} />
								<DecimalInput floatingLabel="Comisión" value={tier.amount} onChange={(value) => updateVolumeTier(index, 'amount', value)} />
								<Button
									variant="secondary"
									className="!w-auto self-end text-red-400"
									aria-label="Eliminar rango por ventas"
									onClick={() => removeVolumeTier(index)}
								>
									<Trash2 className="size-4" />
								</Button>
							</div>
						))}
						<Button
							variant="secondary"
							className="!w-auto"
							disabled={!canAddVolumeTier}
							onClick={() =>
								onChange({
									...draft,
									plan: {
										...draft.plan,
										volume_tiers: [...volumeTiers, { min_sales: nextVolumeMinimum, max_sales: null, amount: 0 }]
									}
								})
							}
						>
							<Plus className="size-4" /> Agregar rango
						</Button>
						{!canAddVolumeTier && <p className="text-xs text-neutral-500">Define “Hasta venta” en el último rango antes de agregar otro.</p>}
						{example && (
							<div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 text-sm text-blue-200">
								<CircleDollarSign className="mr-2 inline size-4" />
								{example}
							</div>
						)}
					</div>
				)}
				<div className="grid gap-4 sm:grid-cols-2">
					<Input floatingLabel="Moneda" placeholder="Moneda" value={currency} disabled onChange={() => undefined} />
					<DecimalInput
						floatingLabel="Mínimo de desembolso"
						value={draft.min_payout_amount}
						onChange={(min_payout_amount) => onChange({ ...draft, min_payout_amount })}
					/>
				</div>
				</section>
			</fieldset>
		</div>
	);
}

function DecimalInput({
	value,
	onChange,
	floatingLabel,
	placeholder = '0.00',
	containerClassName = ''
}: {
	value: number | null;
	onChange: (value: number, raw: string) => void;
	floatingLabel: string;
	placeholder?: string;
	containerClassName?: string;
}) {
	const [raw, setRaw] = useState(value === null || value === 0 ? '' : String(value));

	useEffect(() => {
		const parsedRaw = parseDecimal(raw);
		if (raw && (raw.endsWith('.') || raw.endsWith(','))) return;
		if (value === null && raw === '') return;
		if (value !== null && parsedRaw === value) return;
		setRaw(value === null || value === 0 ? '' : String(value));
	}, [raw, value]);

	return (
		<Input
			floatingLabel={floatingLabel}
			placeholder={placeholder}
			allowedChars="decimal"
			inputMode="decimal"
			value={raw}
			containerClassName={containerClassName}
			onChange={(nextRaw) => {
				const normalized = normalizeDecimalInput(nextRaw);
				setRaw(normalized);
				onChange(parseDecimal(normalized), normalized);
			}}
		/>
	);
}

function IntegerInput({
	value,
	onChange,
	floatingLabel,
	placeholder = '0',
	allowEmpty = false,
	disabled = false
}: {
	value: number | null;
	onChange: (value: number | null) => void;
	floatingLabel: string;
	placeholder?: string;
	allowEmpty?: boolean;
	disabled?: boolean;
}) {
	const [raw, setRaw] = useState(value === null ? '' : String(value));

	useEffect(() => {
		if (value === null && raw === '') return;
		if (value !== null && Number(raw) === value) return;
		setRaw(value === null ? '' : String(value));
	}, [raw, value]);

	return (
		<Input
			floatingLabel={floatingLabel}
			placeholder={placeholder}
			allowedChars="numbers_only"
			inputMode="numeric"
			value={raw}
			disabled={disabled}
			onChange={(nextRaw) => {
				setRaw(nextRaw);
				if (!nextRaw) {
					onChange(allowEmpty ? null : 1);
					return;
				}
				onChange(Number.parseInt(nextRaw, 10));
			}}
		/>
	);
}

function SelectionCard({
	selected,
	icon: Icon,
	title,
	description,
	onClick
}: {
	selected: boolean;
	icon: typeof Store;
	title: string;
	description: string;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(
				'rounded-xl border p-4 text-left transition-colors',
				selected ? 'border-primary-500 bg-primary-500/5 ring-1 ring-primary-500/20' : 'border-neutral-800 bg-neutral-925/50 hover:border-neutral-700'
			)}
		>
			<div className="flex items-center justify-between gap-3">
				<span className="inline-flex items-center gap-2 font-medium text-neutral-100">
					<Icon className="size-4 text-primary-300" /> {title}
				</span>
				<span
					className={cn(
						'size-4 rounded-full border',
						selected ? 'border-primary-300 bg-primary-500 ring-2 ring-primary-500/20' : 'border-neutral-700'
					)}
				/>
			</div>
			<p className="mt-3 text-sm leading-relaxed text-neutral-500">{description}</p>
		</button>
	);
}

function ReviewStep({
	draft,
	currency,
	selectedTargets,
	overlap,
	restrictedEdit
}: {
	draft: SellerCommissionCampaignInput;
	currency: string;
	selectedTargets: SellerCommissionTarget[];
	overlap: SellerCommissionCampaign | null;
	restrictedEdit: boolean;
}) {
	return (
		<div className="space-y-5">
			<div>
				<h3 className="font-medium text-neutral-100">Revisa antes de publicar</h3>
				<p className="mt-1 text-sm text-neutral-500">
					{restrictedEdit
						? 'Confirma la nueva vigencia y las tiendas agregadas. El resto de la configuración no cambiará.'
						: 'Confirma la vigencia, el alcance y quién recibirá el dinero.'}
				</p>
			</div>
			{overlap && (
				<div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300">
					<XCircle className="mr-2 inline size-4" />
					Se solapa con <strong>{overlap.name}</strong>. Debes ajustar la vigencia o el alcance.
				</div>
			)}
			<div className="divide-y divide-neutral-800 overflow-hidden rounded-xl border border-neutral-800 bg-neutral-925/50">
				<ReviewRow label="Nombre" value={draft.name} />
				<ReviewRow label="Vigencia" value={campaignVigencyLabel(draft.starts_at, draft.ends_at, true)} />
				<ReviewRow label="Alcance" value={selectedTargets.map((target) => target.label).join(' · ')} />
				<ReviewRow label="Productos" value={productCategoriesLabel(draft.product_categories)} />
				<ReviewRow label="Comisión" value={commissionDraftLabel(draft, currency)} />
				<ReviewRow
					label="Desembolso"
					value={`${draft.payout_recipient === 'master' ? 'Cobra la tienda master' : 'Cobra cada vendedor'} · mínimo ${money(currency, draft.min_payout_amount)}`}
				/>
			</div>
			<div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 text-sm text-amber-300">
				<AlertCircle className="mr-2 inline size-4" />
				{restrictedEdit
					? 'Las tiendas agregadas comienzan a generar al guardar; no reciben comisiones por ventas anteriores.'
					: 'Después del inicio solo podrás extender o eliminar el cierre, agregar tiendas o detener la campaña.'}
			</div>
		</div>
	);
}

function ReviewRow({ label, value }: { label: string; value: string }) {
	return (
		<div className="grid gap-1 p-4 sm:grid-cols-[140px_1fr]">
			<span className="text-xs font-medium uppercase tracking-wider text-neutral-500">{label}</span>
			<span className="text-sm text-neutral-200">{value || 'Pendiente'}</span>
		</div>
	);
}

function LiveSummary({
	draft,
	currency,
	selectedTargets,
	overlap
}: {
	draft: SellerCommissionCampaignInput;
	currency: string;
	selectedTargets: SellerCommissionTarget[];
	overlap: SellerCommissionCampaign | null;
}) {
	const sections = [
		{
			label: 'Nombre',
			complete: draft.name.trim().length >= 3,
			value: draft.name || 'Pendiente'
		},
		{
			label: 'Vigencia',
			complete: !!draft.starts_at && (draft.ends_at === null || (!!draft.ends_at && new Date(draft.ends_at) > new Date(draft.starts_at))),
			value: campaignVigencyLabel(draft.starts_at, draft.ends_at)
		},
		{
			label: `Alcance · ${selectedTargets.length}`,
			complete: selectedTargets.length > 0,
			value: selectedTargets.length ? selectedTargets.map((target) => target.label).join(' · ') : 'Pendiente'
		},
		{
			label: 'Productos',
			complete: draft.product_categories.length > 0,
			value: productCategoriesLabel(draft.product_categories)
		},
		{
			label: 'Comisión',
			complete: !validateCampaignStep(draft, 3),
			value: commissionDraftLabel(draft, currency)
		},
		{
			label: 'Desembolso',
			complete: draft.min_payout_amount > 0,
			value: `${draft.payout_recipient === 'master' ? 'Cobra la tienda master' : 'Cobra cada vendedor'} · mínimo ${money(currency, draft.min_payout_amount)}`
		}
	];
	return (
		<aside className="border-t border-neutral-800 bg-neutral-950/30 p-5 lg:border-l lg:border-t-0 lg:p-6">
			<h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Resumen en vivo</h3>
			<div className="mt-4 divide-y divide-neutral-800">
				{sections.map((section) => (
					<div key={section.label} className="py-4">
						<p className={cn('inline-flex items-center gap-2 text-xs font-medium', section.complete ? 'text-emerald-300' : 'text-amber-300')}>
							{section.complete ? <Check className="size-3.5" /> : <AlertCircle className="size-3.5" />}
							{section.label}
						</p>
						<p className="mt-2 text-sm leading-relaxed text-neutral-300">{section.value}</p>
					</div>
				))}
			</div>
			<div
				className={cn(
					'mt-4 rounded-lg border p-3 text-xs',
					overlap ? 'border-red-500/20 bg-red-500/5 text-red-300' : 'border-emerald-500/20 bg-emerald-500/5 text-emerald-300'
				)}
			>
				{overlap ? `Se solapa con ${overlap.name}` : 'No se detectan campañas solapadas con esta configuración.'}
			</div>
		</aside>
	);
}

function PayoutRequests({
	requests,
	total,
	filter,
	loading,
	error,
	updating,
	onFilter,
	onRetry,
	onUpdate,
	onDownloadReceipt
}: {
	requests: SellerCommissionPayoutRequest[];
	total: number;
	filter: PayoutFilter;
	loading: boolean;
	error: unknown;
	updating: string | null;
	onFilter: (filter: PayoutFilter) => void;
	onRetry: () => void;
	onUpdate: (request: SellerCommissionPayoutRequest, status: SellerCommissionPayoutTransitionStatus) => void;
	onDownloadReceipt: (request: SellerCommissionPayoutRequest) => void;
}) {
	const [copied, setCopied] = useState('');
	const visible = useMemo(
		() =>
			[...requests].sort(
				(a, b) => payoutOrder(a.status) - payoutOrder(b.status) || new Date(b.requested_at).getTime() - new Date(a.requested_at).getTime()
			),
		[requests]
	);
	const copyValue = async (id: string, value?: string) => {
		if (!value) return;
		await navigator.clipboard.writeText(value);
		setCopied(id);
		window.setTimeout(() => setCopied(''), 1600);
	};
	if (loading) return <AdminListSkeleton />;
	if (error) return <InlineError message="No se pudieron cargar las solicitudes." onRetry={onRetry} />;
	return (
		<div className="space-y-4">
			<div className="flex flex-wrap gap-2">
				{(
					[
						['all', 'Todas'],
						['requested', 'Solicitadas'],
						['processing', 'Procesando'],
						['paid', 'Pagadas'],
						['rejected', 'Rechazadas']
					] as [PayoutFilter, string][]
				).map(([value, label]) => (
					<button
						key={value}
						type="button"
						onClick={() => onFilter(value)}
						className={cn(
							'rounded-lg px-3 py-2 text-xs font-medium',
							filter === value ? 'bg-neutral-800 text-neutral-100' : 'text-neutral-400 hover:bg-neutral-900/50'
						)}
					>
						{label}
						{filter === value && <span className="ml-1 tabular-nums text-neutral-500">{total}</span>}
					</button>
				))}
			</div>
			{visible.length ? (
				visible.map((request) => (
					<PayoutCard
						key={request.id}
						request={request}
						copied={copied}
						updating={updating === request.id}
						onCopy={copyValue}
						onUpdate={onUpdate}
						onDownloadReceipt={onDownloadReceipt}
					/>
				))
			) : (
				<EmptyState icon={ListChecks} title="No hay solicitudes con este estado" description="Las solicitudes nuevas aparecerán aquí." />
			)}
		</div>
	);
}

function PayoutCard({
	request,
	copied,
	updating,
	onCopy,
	onUpdate,
	onDownloadReceipt
}: {
	request: SellerCommissionPayoutRequest;
	copied: string;
	updating: boolean;
	onCopy: (id: string, value?: string) => void;
	onUpdate: (request: SellerCommissionPayoutRequest, status: SellerCommissionPayoutTransitionStatus) => void;
	onDownloadReceipt: (request: SellerCommissionPayoutRequest) => void;
}) {
	const actionable = request.status === 'requested' || request.status === 'processing';
	const rejected = request.status === 'rejected';
	const account = request.bank_account_snapshot;
	return (
		<article
			className={cn(
				'overflow-hidden rounded-xl border bg-neutral-925/50',
				request.status === 'requested' ? 'border-blue-500/40 ring-1 ring-blue-500/15' : 'border-neutral-800'
			)}
		>
			<header className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
				<div className="flex min-w-0 items-center gap-3">
					<div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-neutral-900 text-neutral-400">
						{request.claimant_type === 'master' ? <Store className="size-4" /> : <UserRound className="size-4" />}
					</div>
					<div className="min-w-0">
						<p className="truncate font-medium text-neutral-100">{request.claimant_email || request.seller_email || request.master_store_email}</p>
						<p className="mt-1 truncate text-xs text-neutral-500">
							{request.claimant_type === 'master' ? 'Tienda master' : 'Vendedor'} ·{' '}
							<strong className="text-neutral-300">{request.campaign_name || 'Campaña'}</strong>
						</p>
					</div>
				</div>
				<div className="flex items-center justify-between gap-3 sm:block sm:text-right">
					<span className={cn('rounded-md px-2 py-1 text-xs sm:hidden', payoutStatusClass[request.status])}>{payoutStatus[request.status]}</span>
					<p className="text-xl font-semibold tabular-nums text-neutral-100">{money(request.currency, request.amount)}</p>
					<p className="mt-1 text-xs text-neutral-500">{formatDateTime(request.requested_at)}</p>
				</div>
			</header>
			{!rejected && (
				<div className="grid border-t border-neutral-800 sm:grid-cols-2 lg:grid-cols-3">
					<BankField label="Banco" value={account?.bank_name || 'No disponible'} icon={Landmark} />
					<BankField
						label="Tipo de cuenta"
						value={account?.account_type || 'No disponible'}
						icon={WalletCards}
						copyId={`${request.id}-account-type`}
						copied={copied}
						onCopy={() => onCopy(`${request.id}-account-type`, account?.account_type)}
					/>
					<BankField
						label="Cuenta"
						value={account?.account_number || 'No disponible'}
						icon={CreditCard}
						copyId={`${request.id}-account`}
						copied={copied}
						onCopy={() => onCopy(`${request.id}-account`, account?.account_number)}
					/>
					{account?.interbank_code && (
						<BankField
							label="CCI"
							value={account.interbank_code}
							icon={CreditCard}
							copyId={`${request.id}-cci`}
							copied={copied}
							onCopy={() => onCopy(`${request.id}-cci`, account.interbank_code)}
						/>
					)}
					<BankField label="Titular" value={account?.account_name || 'No disponible'} icon={UserRound} />
					<BankField
						label="Documento"
						value={`${account?.document_type?.toUpperCase() || 'Documento'} ${account?.identification_number || 'no disponible'}`}
						icon={WalletCards}
						copyId={`${request.id}-id`}
						copied={copied}
						onCopy={() => onCopy(`${request.id}-id`, account?.identification_number)}
					/>
				</div>
			)}
			<footer className="flex flex-col gap-3 border-t border-neutral-800 p-4 md:flex-row md:items-center md:justify-between">
				<PayoutPipeline status={request.status} notes={request.notes} />
				<div className="flex shrink-0 flex-wrap gap-2">
					{request.status === 'requested' && (
						<Button variant="secondary" className="!w-auto text-red-400" loading={updating} onClick={() => onUpdate(request, 'rejected')}>
							Rechazar
						</Button>
					)}
					{request.status === 'requested' && (
						<Button variant="primary" className="!w-auto" loading={updating} onClick={() => onUpdate(request, 'processing')}>
							Procesar
						</Button>
					)}
					{request.status === 'processing' && (
						<>
							<Button variant="secondary" className="!w-auto text-red-400" loading={updating} onClick={() => onUpdate(request, 'rejected')}>
								Rechazar
							</Button>
							<Button variant="primary" className="!w-auto" loading={updating} onClick={() => onUpdate(request, 'paid')}>
								Marcar pagada
							</Button>
						</>
					)}
					{request.receipt && (
						<Button variant="secondary" className="!w-auto" onClick={() => onDownloadReceipt(request)}>
							<Download className="size-4" /> Descargar comprobante
						</Button>
					)}
					{!actionable && (
						<span className={cn('rounded-md px-2.5 py-1.5 text-xs', payoutStatusClass[request.status])}>{payoutStatus[request.status]}</span>
					)}
				</div>
			</footer>
		</article>
	);
}

function BankField({
	label,
	value,
	icon: Icon,
	copyId,
	copied,
	onCopy
}: {
	label: string;
	value: string;
	icon: typeof Store;
	copyId?: string;
	copied?: string;
	onCopy?: () => void;
}) {
	return (
		<div className="border-b border-neutral-800 p-4 last:border-b-0 sm:border-r lg:border-b-0 lg:last:border-r-0">
			<p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">{label}</p>
			<div className="mt-2 flex items-center gap-2 text-sm text-neutral-200">
				<Icon className="size-3.5 shrink-0 text-neutral-500" />
				<span className="min-w-0 truncate tabular-nums">{value}</span>
				{onCopy && (
					<button
						type="button"
						onClick={onCopy}
						className="ml-auto inline-flex shrink-0 items-center gap-1 text-xs text-neutral-500 hover:text-neutral-200"
					>
						<Copy className="size-3.5" /> {copied === copyId ? 'Copiado' : 'Copiar'}
					</button>
				)}
			</div>
		</div>
	);
}

function PayoutPipeline({ status, notes }: { status: SellerCommissionPayoutRequest['status']; notes?: string }) {
	if (status === 'rejected')
		return (
			<p className="flex items-center gap-2 text-xs text-red-300">
				<XCircle className="size-3.5" /> Rechazada{notes ? ` · ${notes}` : ''}
			</p>
		);
	const steps: SellerCommissionPayoutRequest['status'][] = ['requested', 'processing', 'paid'];
	const current = steps.indexOf(status);
	const semanticClass = {
		requested: 'text-blue-300',
		processing: 'text-amber-300',
		paid: 'text-emerald-300',
		rejected: 'text-red-300'
	};
	const semanticDot = {
		requested: 'bg-blue-400',
		processing: 'bg-amber-400',
		paid: 'bg-emerald-400',
		rejected: 'bg-red-400'
	};
	return (
		<div className="flex flex-wrap items-center gap-2 text-xs">
			{steps.map((step, index) => (
				<div key={step} className="flex items-center gap-2">
					<span className={cn('inline-flex items-center gap-1', index <= current ? semanticClass[step] : 'text-neutral-600')}>
						<span className={cn('size-1.5 rounded-full', index <= current ? semanticDot[step] : 'border border-neutral-700')} />
						{payoutStatus[step]}
					</span>
					{index < steps.length - 1 && <span className="h-px w-5 bg-neutral-800" />}
				</div>
			))}
		</div>
	);
}

function PayoutConfirmation({
	pending,
	updating,
	onClose,
	onConfirm
}: {
	pending: {
		request: SellerCommissionPayoutRequest;
		status: SellerCommissionPayoutTransitionStatus;
	};
	updating: boolean;
	onClose: () => void;
	onConfirm: (notes: string, receipt?: File) => void;
}) {
	const [notes, setNotes] = useState('');
	const [receipt, setReceipt] = useState<File | undefined>();
	const paid = pending.status === 'paid';
	const rejected = pending.status === 'rejected';
	return (
		<Dialog
			title={paid ? '¿Marcar esta solicitud como pagada?' : rejected ? '¿Rechazar esta solicitud?' : 'Actualizar solicitud'}
			onClose={onClose}
			cerrarButton={false}
			className="w-[min(520px,92vw)]"
		>
			<div className="space-y-4">
				<p className="text-sm text-neutral-300">
					{paid ? (
						<>
							Confirma que la transferencia de <strong className="tabular-nums">{money(pending.request.currency, pending.request.amount)}</strong>{' '}
							a <strong>{pending.request.claimant_email || pending.request.seller_email || pending.request.master_store_email}</strong> ya fue
							realizada manualmente.
						</>
					) : (
						<>
							La solicitud cambiará a <strong>{payoutStatus[pending.status]}</strong>.
						</>
					)}
				</p>
				{rejected && (
					<label className="block space-y-2 text-sm text-neutral-300">
						<span>Motivo del rechazo</span>
						<textarea
							value={notes}
							onChange={(event) => setNotes(event.target.value.slice(0, 1000))}
							placeholder="Explique por qué se rechaza para que quede en el historial."
							className="min-h-24 w-full resize-y rounded-lg border border-neutral-800 bg-neutral-950/50 p-3 text-sm text-neutral-200 outline-none placeholder:text-neutral-600 focus:border-primary-500/50"
						/>
					</label>
				)}
				{paid && (
					<>
						<label className="block space-y-2 text-sm text-neutral-300">
							<span>Comprobante PDF <span className="text-neutral-500">(opcional, máximo 10 MB)</span></span>
							<span className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-neutral-700 bg-neutral-950/50 p-3 text-sm text-neutral-400 hover:border-neutral-600">
								<FileText className="size-4 shrink-0 text-primary-300" />
								<span className="min-w-0 truncate">{receipt?.name || 'Seleccionar comprobante'}</span>
								<input
									type="file"
									accept="application/pdf,.pdf"
									className="sr-only"
									onChange={(event) => setReceipt(event.target.files?.[0])}
								/>
							</span>
						</label>
						{receipt && !validPayoutReceipt(receipt) && (
							<p className="text-xs text-red-300">Selecciona un PDF válido de máximo 10 MB.</p>
						)}
						<div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-3 text-xs leading-relaxed text-amber-300">
							<AlertCircle className="mr-2 inline size-4" />
							Esta acción solo registra el pago; no ejecuta ninguna transferencia bancaria. Es un estado terminal y no se puede reabrir.
						</div>
					</>
				)}
				<div className="flex justify-end gap-2">
					<Button variant="text" className="!w-auto" onClick={onClose}>
						Cancelar
					</Button>
					<Button
						variant={rejected ? 'destructive' : 'primary'}
						className="!w-auto"
						loading={updating}
						disabled={(rejected && notes.trim().length < 3) || !!(receipt && !validPayoutReceipt(receipt))}
						onClick={() => onConfirm(notes, receipt)}
					>
						{paid ? 'Sí, ya transferí — marcar pagada' : `Confirmar ${payoutStatus[pending.status].toLowerCase()}`}
					</Button>
				</div>
			</div>
		</Dialog>
	);
}

function InlineError({ message, onRetry }: { message: string; onRetry: () => void }) {
	return (
		<div className="flex flex-col items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300 light:text-red-700 sm:flex-row sm:items-center sm:justify-between">
			<span className="inline-flex items-center gap-2">
				<AlertCircle className="size-4" />
				{message}
			</span>
			<Button variant="secondary" className="!w-auto" onClick={onRetry}>
				<RefreshCw className="size-4" /> Reintentar
			</Button>
		</div>
	);
}

function EmptyState({ icon: Icon, title, description, compact = false }: { icon: typeof Store; title: string; description: string; compact?: boolean }) {
	return (
		<div
			className={cn(
				'flex flex-col items-center justify-center rounded-xl border border-dashed border-neutral-700 bg-neutral-925/30 p-6 text-center',
				compact ? 'min-h-[150px]' : 'min-h-[220px]'
			)}
		>
			<Icon className="size-6 text-neutral-500" />
			<p className="mt-3 font-medium text-neutral-200">{title}</p>
			<p className="mt-1 text-sm text-neutral-500">{description}</p>
		</div>
	);
}

function AdminListSkeleton() {
	return (
		<div className="animate-pulse space-y-3">
			<div className="flex gap-2">
				{[1, 2, 3, 4].map((item) => (
					<div key={item} className="h-9 w-24 rounded-lg bg-neutral-800/50" />
				))}
			</div>
			{[1, 2, 3].map((item) => (
				<div key={item} className="h-36 rounded-xl bg-neutral-900/50" />
			))}
		</div>
	);
}

function validateCampaignStep(campaign: SellerCommissionCampaignInput, step: CampaignStep, editingCampaign: SellerCommissionCampaign | null = null) {
	if (step === 1) {
		if (campaign.name.trim().length < 3) return 'Ingrese un nombre para la campaña.';
		if (!campaign.starts_at) return 'Seleccione la fecha de inicio.';
		if (campaign.ends_at === '') return 'Seleccione la fecha de cierre o active la opción sin expiración.';
		if (!isRestrictedCampaignEdit(editingCampaign) && new Date(campaign.starts_at) <= new Date()) return 'La fecha de inicio debe estar en el futuro.';
		if (campaign.ends_at && new Date(campaign.ends_at) <= new Date(campaign.starts_at)) return 'La fecha de cierre debe ser posterior al inicio.';
		if (editingCampaign && isRestrictedCampaignEdit(editingCampaign)) {
			if (campaign.ends_at && new Date(campaign.ends_at) <= new Date()) return 'La nueva fecha de cierre debe estar en el futuro.';
			if (editingCampaign.ends_at === null && campaign.ends_at !== null) return 'Una campaña sin vencimiento no se puede acortar.';
			if (campaign.ends_at && editingCampaign.ends_at && new Date(campaign.ends_at) < new Date(editingCampaign.ends_at))
				return 'La fecha de cierre no se puede acortar.';
		}
	}
	if (step === 2 && !campaign.target_keys.length) return 'Seleccione al menos una tienda o sucursal.';
	if (step === 3) {
		if (!campaign.product_categories.length) return 'Seleccione al menos un tipo de producto.';
		if (campaign.payout_recipient !== 'seller' && campaign.payout_recipient !== 'master') return 'Seleccione quién solicitará el desembolso.';
		if (campaign.min_payout_amount <= 0) return 'El mínimo de desembolso debe ser mayor a cero.';
		if (campaign.plan.mode === 'fixed_per_sale' && campaign.plan.fixed_amount <= 0) return 'La comisión por venta debe ser mayor a cero.';
		if (campaign.plan.mode === 'price_tiers') {
			if (!campaign.plan.tiers.length) return 'Agregue al menos un rango por precio.';
			for (const tier of campaign.plan.tiers)
				if (tier.amount <= 0 || tier.min_price < 0 || (tier.max_price !== null && tier.max_price < tier.min_price)) return 'Revise los rangos por precio.';
		}
		if (campaign.plan.mode === 'sales_volume_tiers') {
			const tiers = campaign.plan.volume_tiers || [];
			if (!tiers.length) return 'Agregue al menos un rango por cantidad de ventas.';
			if (tiers[0].min_sales !== 1) return 'El primer rango debe comenzar en la venta 1.';
			for (let index = 0; index < tiers.length; index += 1) {
				const tier = tiers[index];
				if (!Number.isInteger(tier.min_sales) || tier.min_sales < 1 || tier.amount <= 0) return 'Revise los rangos por cantidad de ventas.';
				if (tier.max_sales !== null && (!Number.isInteger(tier.max_sales) || tier.max_sales < tier.min_sales)) return 'Revise los rangos por cantidad de ventas.';
				if (index > 0 && (tiers[index - 1].max_sales === null || tier.min_sales !== (tiers[index - 1].max_sales || 0) + 1)) return 'Los rangos por ventas deben ser consecutivos.';
			}
		}
	}
	return null;
}

function validateCampaign(campaign: SellerCommissionCampaignInput, editingCampaign: SellerCommissionCampaign | null = null) {
	return (
		validateCampaignStep(campaign, 1, editingCampaign) ||
		validateCampaignStep(campaign, 2, editingCampaign) ||
		validateCampaignStep(campaign, 3, editingCampaign)
	);
}

function canEditCampaign(campaign: SellerCommissionCampaign) {
	if (campaign.status === 'upcoming' || campaign.status === 'active') return true;
	if (campaign.status !== 'ended') return false;
	return !!campaign.ends_at && Date.now() - new Date(campaign.ends_at).getTime() <= 48 * 60 * 60 * 1000;
}

function isRestrictedCampaignEdit(campaign: SellerCommissionCampaign | null) {
	return campaign?.status === 'active' || campaign?.status === 'ended';
}

function normalizeDecimalInput(value: string) {
	const normalized = value.replace(',', '.');
	const [whole, ...fractionParts] = normalized.split('.');
	if (!fractionParts.length) return whole;
	return `${whole}.${fractionParts.join('')}`;
}

function parseDecimal(value: string) {
	const parsed = Number(normalizeDecimalInput(value));
	return Number.isFinite(parsed) ? parsed : 0;
}

function findOverlappingCampaign(draft: SellerCommissionCampaignInput, campaigns: SellerCommissionCampaign[], editingId: string | null) {
	if (!draft.starts_at || draft.ends_at === '' || !draft.target_keys.length) return null;
	const start = new Date(draft.starts_at).getTime();
	const end = draft.ends_at ? new Date(draft.ends_at).getTime() : Number.POSITIVE_INFINITY;
	return (
		campaigns.find((campaign) => {
			if (campaign.id === editingId || (!campaign.is_enabled && !campaign.stopped_at)) return false;
			const configuredEnd = campaign.ends_at ? new Date(campaign.ends_at).getTime() : Number.POSITIVE_INFINITY;
			const campaignEnd = campaign.stopped_at ? Math.min(configuredEnd, new Date(campaign.stopped_at).getTime()) : configuredEnd;
			if (start >= campaignEnd || new Date(campaign.starts_at).getTime() >= end) return false;
			return draft.target_keys.some((first) => campaign.target_keys.some((second) => targetKeysOverlap(first, second)));
		}) || null
	);
}

function targetKeysOverlap(first: string, second: string) {
	const firstParts = first.split(':');
	const secondParts = second.split(':');
	const firstMaster = firstParts[1];
	const secondMaster = secondParts[1];
	if (!firstMaster || firstMaster !== secondMaster) return false;
	if (firstParts[0] === 'master' || secondParts[0] === 'master') return true;
	return firstParts.slice(2).join(':') === secondParts.slice(2).join(':');
}

function tierExample(draft: SellerCommissionCampaignInput, currency: string) {
	const tier = draft.plan.tiers.find((item) => item.amount > 0 && item.min_price >= 0 && (item.max_price === null || item.max_price >= item.min_price));
	if (!tier) return '';
	const price = tier.max_price === null ? tier.min_price + 10 : Math.round(((tier.min_price + tier.max_price) / 2) * 100) / 100;
	return `Ejemplo: un equipo de ${money(currency, price)} cae en este rango y genera ${money(currency, tier.amount)} de comisión. Las ventas fuera de todos los rangos no generan comisión.`;
}

function volumeTierExample(draft: SellerCommissionCampaignInput, currency: string) {
	const tier = (draft.plan.volume_tiers || []).find(
		(item) => item.amount > 0 && item.min_sales >= 1 && (item.max_sales === null || item.max_sales >= item.min_sales)
	);
	if (!tier) return '';
	const saleNumber = tier.max_sales === null ? tier.min_sales : Math.round((tier.min_sales + tier.max_sales) / 2);
	return `Ejemplo: la venta ${saleNumber} de ese vendedor genera ${money(currency, tier.amount)}. Las ventas anteriores conservan el monto que recibieron al registrarse.`;
}

function commissionDraftLabel(draft: SellerCommissionCampaignInput, currency: string) {
	if (draft.plan.mode === 'fixed_per_sale') return `Monto fijo · ${money(currency, draft.plan.fixed_amount)} por venta`;
	const tiers = draft.plan.mode === 'sales_volume_tiers' ? draft.plan.volume_tiers || [] : draft.plan.tiers;
	if (!tiers.length) return 'Rangos pendientes';
	const amounts = tiers.map((tier) => tier.amount).filter((amount) => amount > 0);
	return `${tiers.length} rangos por ${draft.plan.mode === 'sales_volume_tiers' ? 'ventas' : 'precio'}${amounts.length ? ` · ${money(currency, Math.min(...amounts))}–${money(currency, Math.max(...amounts))}` : ''}`;
}

function campaignProgress(campaign: SellerCommissionCampaign) {
	if (!campaign.ends_at) return 100;
	const start = new Date(campaign.starts_at).getTime();
	const end = new Date(campaign.ends_at).getTime();
	return Math.max(0, Math.min(100, ((Date.now() - start) / (end - start)) * 100));
}

function productCategoriesLabel(categories: SellerCommissionProductCategory[]) {
	const labels: Record<SellerCommissionProductCategory, string> = {
		phones: 'Celulares',
		iphones: 'iPhones',
		other: 'Otros'
	};
	const selected = (['phones', 'iphones', 'other'] as SellerCommissionProductCategory[])
		.filter((category) => categories.includes(category))
		.map((category) => labels[category]);
	return selected.join(' · ') || 'Sin productos seleccionados';
}

function campaignOrder(status: SellerCommissionCampaignStatus) {
	return status === 'active' ? 0 : 1;
}

function payoutOrder(status: SellerCommissionPayoutRequest['status']) {
	return { requested: 0, processing: 1, paid: 2, rejected: 3 }[status];
}

function defaultCampaignEnd(startsAt: string) {
	const start = startsAt ? new Date(startsAt) : new Date();
	const end = new Date(start.getTime() + 30 * 86_400_000);
	return end.toISOString();
}

function campaignVigencyLabel(startsAt: string, endsAt: string | null, detailed = false) {
	if (!startsAt) return 'Pendiente';
	const start = detailed ? formatDateTime(startsAt) : shortDate(startsAt);
	if (endsAt === null) return `${start} — Sin vencimiento (BAU)`;
	if (!endsAt) return 'Pendiente';
	return `${start} — ${detailed ? formatDateTime(endsAt) : shortDate(endsAt)}`;
}

function relativeEnd(value: string) {
	const days = Math.max(0, Math.ceil((new Date(value).getTime() - Date.now()) / 86_400_000));
	return days === 1 ? 'mañana' : `en ${days} días`;
}

function shortDate(value: string | null) {
	if (!value) return 'Pendiente';
	return new Date(value).toLocaleDateString([], {
		month: 'short',
		day: 'numeric',
		year: 'numeric'
	});
}

function formatDateTime(value: string | null) {
	if (!value) return 'Pendiente';
	return new Date(value).toLocaleString([], {
		dateStyle: 'medium',
		timeStyle: 'short'
	});
}

function money(currency: string, amount: number) {
	return `${currency}${format_currency(Math.abs(amount || 0))}`;
}
